import { fetchTokenAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { domainToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

const REGION_HOSTS: Record<string, string> = {
  northAmerica: "app.ninjarmm.com",
  northAmericaUs2: "us2.ninjarmm.com",
  europe: "eu.ninjarmm.com",
  oceania: "oc.ninjarmm.com",
  canada: "ca.ninjarmm.com",
  japan: "jp.ninjarmm.com",
  gov: "fed.ninjarmm.com",
};

const ninjaoneHost = (region: string | undefined) =>
  REGION_HOSTS[region ?? ""] ?? REGION_HOSTS.northAmerica!;

export const ninjaone = defineConnector({
  identifier: "ninjaone",
  name: "NinjaOne",
  tagLine: "IT management and RMM",
  logo,
  protocol: "http",
  tags: ["it"],
  defaultUrlPattern: ({ region }) => domainToRegex(ninjaoneHost(region)),
  auth: (app) =>
    fetchTokenAuth({
      request: {
        url: `https://${ninjaoneHost(app.configuration.region)}/ws/oauth/token`,
        formBody: {
          grant_type: "client_credentials",
          client_id: app.secret.clientId,
          client_secret: app.secret.clientSecret,
          scope: "monitoring",
        },
      },
      extract: { from: "jsonPath", paths: ["access_token"] },
      apply: { kind: "header", scheme: "Bearer" },
    }),
  steps: [
    {
      title: "Create API credentials",
      items: [
        {
          kind: "markdown",
          content:
            "In NinjaOne, go to **Administration → Apps → API** and create an **API Services (machine-to-machine)** app with the **Monitoring** scope and **Client Credentials** grant type. Note the client ID and client secret.",
        },
      ],
    },
    {
      title: "Enter your region and credentials",
      items: [
        {
          kind: "select",
          label: "Region",
          required: true,
          defaultValue: "northAmerica",
          target: { kind: "configuration", field: "region" },
          options: [
            { value: "northAmerica", label: "North America" },
            { value: "northAmericaUs2", label: "North America (US2)" },
            { value: "europe", label: "Europe" },
            { value: "oceania", label: "Oceania" },
            { value: "canada", label: "Canada" },
            { value: "japan", label: "Japan" },
            { value: "gov", label: "Gov / FedRAMP" },
          ],
        },
        {
          kind: "input",
          label: "Client ID",
          required: true,
          target: { kind: "secrets", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          required: true,
          target: { kind: "secrets", field: "clientSecret" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ region }) => `https://${ninjaoneHost(region)}/v2/organizations`,
  },
});
